<template>
	<el-row>
		<el-col :span="18">
			<div class="base-form">				
				<h2>Сервисные компонеты</h2>		
				<el-button type="primary" :icon="circleIcon" size="large" @click="onAddComponentClick">
					Новый сервисный компонент
				</el-button>
			</div>
		</el-col>
	</el-row>
	
	<div class="base-form">			
		<el-row :gutter="8" style="width: 100%;">
			<el-col :span="24">
				<el-input v-model="tableSearchQuery" style="max-width: 600px" clearable placeholder="Поиск по названию компонета (узла)"
					class="input-with-select">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</el-col>
		</el-row>
		<el-row style="width: 100%;">
			<el-col :span="18">
				<div style="margin: 1rem 0">
					<el-select v-model="tableComponentGroupsFilter" multiple clearable placeholder="Фильтр по группам комонентов (узлов)" @change="onTableComponentGroupsFilterChange">
						<el-option v-for="e in componentGroups"
							:key="e.value"
							:value="e.value"
							:label="e.label">
						</el-option>
					</el-select>
				</div>
			</el-col>
		</el-row>
	</div>

	<el-row>
		<el-col :span="24">
			<el-table
				row-key="id" 				
				v-loading="tableLoading"
				sortable="custom"
				:data="tableData"				
				:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"				
				@sort-change="onTableSortingChange"
				@row-click="onTableRowClick">
				<el-table-column v-if="!isPickerMode" type="selection" />
				<el-table-column v-if="isPickerMode" width="100">
					<template #default="scope">
						<el-button
							size="small"
							type="primary"
							@click="(e) => onPickJobComponentClick(scope.row, e)">
							Выбрать
						</el-button>
					</template>
				</el-table-column>
				<el-table-column label="Компонент" prop="name" sortable column-key="name" />
				<el-table-column label="Группа компонентов" prop="componentGroup.name" sortable column-key="group" />				
				<el-table-column label="Операция по-умолчанию" prop="defaultOperation.name" column-key="operation" />
				<el-table-column label="Описание" prop="description" column-key="description" />		
				<el-table-column v-if="!isPickerMode" align="right">
					<template #default="scope">
						<el-button
							size="small"
							type="danger"
							@click="(e) => onDeleteJobComponentClick(scope.row.id, scope.row.name, e)">
							Удалить
						</el-button>
					</template>
				</el-table-column>
			</el-table>
		</el-col>
	</el-row>
	<el-row v-if="isPickerMode">
		<el-col>
			<div class="base-form">
				<el-button @click="onCloseCurrentTabClick">
					Отмена
				</el-button>
			</div>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { CirclePlus, Search } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElMessageBox, ElNotification } from 'element-plus';
import axiosHelper from '@/helpers/axiosHelper';
import ApplicationTab from '@/models/ApplicationTab';
import TabsDictionary from '@/variables/tabs';
import store from '@/store';
import EventHelper from '@/helpers/eventsHelper';
import { WatchStopHandle, watch } from 'vue';
import ServiceJobOperationModel from '@/models/ServiceJobOperationModel';
import PickerConfigurationModel from '@/models/PickerConfigurationModel';
import ServiceJobComponentModel from '@/models/ServiceJobComponentModel';
import LabelValuePair from '@/models/LabelValuePair';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink
	},
	props: {
		params: Array
	}
})
export default class ServiceJobComponentsView extends Vue {
	/** params */
	params!: Array<any>;

	/** constants */	
	isPickerMode = false;
	onPickedCallback: Function | null = null;
	circleIcon = CirclePlus;
	searchIcon = Search;
	jobComponentsGroup: Array<any> = [];
	
	/** view-models */
	tableLoading = false;
	tableData: Array<ServiceJobComponentModel> = [];
	tableSearchQuery = '';
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	tableSortColumn = 'name';
	tableSortOrder = 'ascending';
	
	tableComponentGroupsFilter: Array<number> = [];
	componentGroups: Array<LabelValuePair<string, number>> = [];

	watchTableSearchQuery: WatchStopHandle | null = null;

	mounted() {
		this.initView();
		this.fetchJobComponentsAsync();
		this.fetchJobComponentsGroupsAsync();

		this.watchTableSearchQuery = watch(
			() => this.tableSearchQuery,
			() => this.fetchJobComponentsAsync()
		);

		EventHelper.setEventListener('jobComponentsUpdate',  this.fetchJobComponentsAsync);
		EventHelper.setEventListener('jobComponentGroupsUpdate', this.fetchJobComponentsGroupsAsync);
	}

	unmounted() {
		if (this.watchTableSearchQuery) {
			this.watchTableSearchQuery();
		}

		EventHelper.removeEventListener('jobComponentsUpdate', this.fetchJobComponentsAsync);
		EventHelper.removeEventListener('jobComponentGroupsUpdate', this.fetchJobComponentsGroupsAsync);
	}

	initView() {
		const pickerModeArgument = this.params?.length ? this.params[0] as PickerConfigurationModel : null;
		/** check if picker mode activated */
		if (pickerModeArgument && 
			pickerModeArgument.isPickerModeEnabled === true &&
			pickerModeArgument.onItemPickedCallback) {
			this.isPickerMode = true;
			this.onPickedCallback = pickerModeArgument.onItemPickedCallback;
		}
	}

	onTableRowClick(row: ServiceJobOperationModel) {
		if (this.isPickerMode) {
			return;
		}
		const viewTab = TabsDictionary.jobComponentView;		
		viewTab.params = [ row, false ];
		store.commit('addTab', viewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onAddComponentClick() {
		const addTab = TabsDictionary.jobComponentAdd;
		const addNewComponent = true;
		addTab.params = [ null, addNewComponent ];
		store.commit('addTab', addTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	async fetchJobComponentsAsync() {		
		try {
			this.tableLoading = true;			
			const filterObject = {
				orderBy: this.tableSortColumn ? this.tableSortColumn : 'name',
				orderAsc: this.tableSortOrder == 'ascending' ? true : false,
				query: this.tableSearchQuery,
				groups: this.tableComponentGroupsFilter?.length ? this.tableComponentGroupsFilter.join(',') : null
			};
			const jwt = store.getters.jwt;
			const usersHttpClient = axiosHelper.createInstance(jwt);
			const response = await usersHttpClient.get('/api/servicejobcomponents', { params: filterObject });
			if (!response.data?.length) {
				this.tableData = [];
				return;
			}

			this.tableData = response.data.map((_: any) => new ServiceJobComponentModel(_));
		} catch (e: any) {
			if (e.code == 'ERR_NETWORK') {
				ElNotification({
					message: `Ошибка соединения, не удалось загрузить список сервисных компонетов (узлов)`,
					type: 'error',
				});
			} else {
				ElNotification({
					message: `Ошибка загрузка списка сервисных компонетов (узлов)`,
					type: 'error',
				});
			}
			console.error('Error loading service components list', e)
		} finally {
			this.tableLoading = false;
		}
	}

	async fetchJobComponentsGroupsAsync() {
		try {
			const jwt = store.getters.jwt;
			const jobOperationsHttpClient = axiosHelper.createInstance(jwt);
			const response = await jobOperationsHttpClient.get('/api/servicejobcomponentgroups');
			this.componentGroups = response.data.map((_: any) => new LabelValuePair({
				label: _.name,
				value: _.id
			}));
			
		} catch (e) {						
			console.error(`Error requesting service-job-component-groups`, e)
		}		
	}

	async deleteJobComponentAsync(componentId: number) {
		try {
			this.tableLoading = true;
			const jwt = store.getters.jwt;
			const jobOperationsHttpClient = axiosHelper.createInstance(jwt);
			await jobOperationsHttpClient.delete(`/api/servicejobcomponents/${componentId}`);
			ElNotification({
				title: 'Сервисный комонент удален',
				type: 'success',
			});	
			EventHelper.dispatchEvent('jobComponentsUpdate');
		} catch (e) {			
			ElNotification({
				title: 'Ошибка, сервисный комонент не был удален',
				type: 'error',
			});	
			console.error(`Error deleting service-job-component #${componentId}`, e)
		} finally {
			this.tableLoading = false;
		}		
	}

	async onDeleteJobComponentClick(operationId: number, operationName: string, e: Event) {
		e.stopPropagation();

		const relatedJobIdsToCategory = await this.getRelatedJobIdsAsync(operationId);

		if (relatedJobIdsToCategory === null) {
			ElNotification({
				message: 'Ошибка удаления сервисного компонента',
				type: 'error',
			});
			return;
		}

		if (relatedJobIdsToCategory.length == 0) {
			ElMessageBox.confirm(
				`Компонент '${operationName}' будет удален`,
				{
					confirmButtonText: 'OK',
					cancelButtonText: 'Отмена',
					type: 'error',
				}
			)
			.then(() => this.deleteJobComponentAsync(operationId));
		} else {
			ElMessageBox.alert(
				`Невозможно удалить компонент '${operationName}', на него ссылаются сервисные работы (${relatedJobIdsToCategory.length})`,
				'Удаление'
			);
		}
	}

	async getRelatedJobIdsAsync(componentId: number): Promise<Array<number> | null> {
		try {
			const jwt = store.getters.jwt;
			const jobCategoriesHttpClient = axiosHelper.createInstance(jwt);
			const jobCategoriesResponse = await jobCategoriesHttpClient.get(`/api/servicejobcomponents/rels/${componentId}`);
			return jobCategoriesResponse.data.map((_: number) => _);
		} catch (e) {			
			console.error(`Error checking service-job-component #${componentId} for related jobs`, e);
			return null;
		}
	}

	onTableSortingChange(a: any) {
		this.tableSortColumn = a.prop ?? null;
		this.tableSortOrder == a.order;
		this.fetchJobComponentsAsync();
	}

	/** handles callback and passes selected operations if picker-mode enabled */	
	onPickJobComponentClick(component: ServiceJobComponentModel, e: Event) {
		e.stopPropagation();
		if (!this.onPickedCallback) {
			return;
		}
		this.onPickedCallback(component);
	}

	onCloseCurrentTabClick() {		
		const currentTabId = store.getters.tab;
		store.commit('removeTab', currentTabId);
	}

	onTableComponentGroupsFilterChange() {
		this.fetchJobComponentsAsync();
	}
}
</script>